import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Route } from "react-router-dom";
import { RootState } from "../app/features/store/store";
import Layout from "../components/common/layout/Layout";
import { PAGES } from "../enums/PagesEnum";
import ManifestDashboard from "../pages/Manifest/StoreManifest/Modals/ManifestDashboard";
import StoreSelection from "../pages/Manifest/StoreManifest/StoreSelection";
import { checkPageAndPagePath } from "../pages/checkPagePermissions";
import Clients from "../pages/clients/Clients";
import DedicatedDeliveryDashboard from "../pages/dedicatedDelivery/DedicatedDelivery";
import BeansIntegration from "../pages/beansIntegration/BeansIntegration";
import DedicatedDeliveryActions from "../pages/dedicatedDelivery/actions/Actions";
import Error from "../pages/error/Error";
import EventType from "../pages/eventType/EventType";
import Fdp from "../pages/fdp/Fdp";
import FdpZipcode from "../pages/fdpZipcode/FdpZipcode";
import GcImport from "../pages/gcImport/GcImport";
import Home from "../pages/home/Home";
import RequireAuth from "../pages/login/RequireAuth";
import MaxAttempts from "../pages/maxAttempts/MaxAttempts";
import MiddleMile from "../pages/middleMile/MiddleMile";
import MissingManifest from "../pages/missingManifest/MissingManifest";
import OutOfArea from "../pages/outOfArea/OutOfArea";
import Packages from "../pages/packages/Packages";
import PackagesArrived from "../pages/packagesArrived/PackagesArrived";
import PackagesForReview from "../pages/packagesForReview/PackagesForReview";
import ParcelCenters from "../pages/parcelCenters/ParcelCenters";
import Receiving from "../pages/receiving/Receiving";
import ResetPassword from "../pages/resetPassword/ResetPassword";
import ReturnToStation from "../pages/returnToStation/ReturnToStation";
import Role from "../pages/role/Role";
import RouteProgressDedicated from "../pages/routeProgressDedicated/RouteProgressDedicated";
import RouteReport from "../pages/routeReport/RouteReport";
import RtsScanModule from "../pages/rtsScanModule/RtsScanModule";
import Shipments from "../pages/shipments/Shipments";
import User from "../pages/user/User";
import VehicleLoadScan from "../pages/vehicleLoadScan/VehicleLoadScan";
import ZipCoverage from "../pages/zipCoverage/ZipCoverage";
import RouteProgressDedicatedDashboard from "../pages/routeProgressDedicated/RouteProgressDedicatedDashboard";
import FileManager from "../pages/sftp/FileManager";
import FdpInvoiceList from "../pages/fdp/invoices/FdpInvoiceList";
import KnowledgehubPage from "../components/common/knowledgehub/pages/KnowledgehubPage";

export default function AppRoutes(): React.ReactNode {
  const pagePermissions = useSelector(
    (state: RootState) =>
      state.login?.authResponse?.userSettings?.pagePermissions
  );

  const isResetPasswordRequired = useSelector(
    (state: RootState) => state.login?.authResponse?.isResetPasswordRequired
  );

  const fdpDetails = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails
  );

  if (isResetPasswordRequired) {
    return (
      <Route path="/*" element={<ResetPassword forceResetPassword={true} />} />
    );
  }

  if (!pagePermissions || pagePermissions.length === 0) {
    return (
      <Route path="/" element={<RequireAuth />}>
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Error />} />
        </Route>
        <Route
          path="/reset-password"
          element={<ResetPassword onDemandResetPassword={true} />}
        />
      </Route>
    );
  } else {
    return (
      <Route path="/" element={<RequireAuth />}>
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          {/* zipCoverage */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.ZIP_COVERAGE
          ) && <Route path="/zipCoverage" element={<ZipCoverage />} />}
          {/* clients */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.CLIENTS) && (
            <Route path="/clients" element={<Clients />} />
          )}
          {/* parcelCenters */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.PARCEL_CENTERS
          ) && <Route path="/parcelCenters" element={<ParcelCenters />} />}
          {/* fdp */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.FDP) && (
            <Route path="/fdp" element={<Fdp />} />
          )}
          {/* fdpZipCode */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.FDP_ZIPCODE
          ) && <Route path="/fdpZipCode" element={<FdpZipcode />} />}
          {/* gcImport */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.GC_IMPORT
          ) && <Route path="/gcImport" element={<GcImport />} />}
          {/* shipments */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.SHIPMENTS
          ) && <Route path="/shipments" element={<Shipments />} />}
          {/* packages */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.PACKAGES) && (
            <Route path="/packages" element={<Packages />} />
          )}
          {/* outOfArea */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.OUT_OF_AREA
          ) && <Route path="/outOfArea" element={<OutOfArea />} />}
          {/* packagesArrived */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.PACKAGES_ARRIVED
          ) && <Route path="/packagesArrived" element={<PackagesArrived />} />}
          {/* packagesForReview */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.PACKAGES_FOR_REVIEW
          ) && (
              <Route path="/packagesForReview" element={<PackagesForReview />} />
            )}
          {/* maxAttempts */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.MAX_ATTEMPT
          ) && <Route path="/maxAttempts" element={<MaxAttempts />} />}
          {/* missingManifest */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.MISSING_MANIFEST
          ) && <Route path="/missingManifest" element={<MissingManifest />} />}
          {/* user */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.USER) && (
            <Route path="/user" element={<User />} />
          )}
          {/* role */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.ROLE) && (
            <Route path="/role" element={<Role />} />
          )}
          {/* routeReport */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.ROUTE_REPORT
          ) && <Route path="/routeReport" element={<RouteReport />} />}
          {/* middleMile */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.MIDDLE_MILE
          ) && <Route path="/middleMile" element={<MiddleMile />} />}
          {/* receiving */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.RECEIVING
          ) && <Route path="/receiving" element={<Receiving />} />}
          {/* Store Manifest */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.STORE_MANIFEST
          ) && (
              <>
                <Route
                  path="/manifest-store-selection"
                  element={<StoreSelection />}
                />
                <Route
                  path="/store-manifest/:storePrimaryId"
                  element={<ManifestDashboard />}
                />
              </>
            )}
          {/* returnToStation */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.RETURN_TO_STATION
          ) && <Route path="/returnToStation" element={<ReturnToStation />} />}
          {/* add RTS */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.ADD_RETURN_TO_STATION
          ) && <Route path="/addRTS" element={<RtsScanModule />} />}
          {/* eventType */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.EVENT_TYPE
          ) && <Route path="/eventType" element={<EventType />} />}
          {/* vehicleLoadScan */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.VEHICLE_LOAD_SCAN
          ) && <Route path="/vehicleLoadScan" element={<VehicleLoadScan />} />}
          {/* Dedicated Delivery Dashboard */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.DEDICATED_DELIVERY
          ) && (
              <Route
                path="/dedicated-delivery"
                element={<DedicatedDeliveryDashboard />}
              />
            )}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.DEDICATED_DELIVERY
          ) && (
              <Route
                path="/dedicated-delivery-overview"
                element={<DedicatedDeliveryActions />}
              />
            )}
          {/* Route Progress - Dedicated Dashboard */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.ROUTE_PROGRESS_DEDICATED
          ) && (
              <Route
                path="/route-progress-dedicated"
                element={<RouteProgressDedicatedDashboard />}
              />
            )}

          {/* Route Progress - Dedicated Dashboard */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.ROUTE_PROGRESS_DEDICATED
          ) && (
              <Route
                path="/route-progress-dedicated-overview"
                element={<RouteProgressDedicated />}
              />
            )}
          {/* Route Progress - Dedicated Dashboard */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.INVOICE_LIST
          ) && (
              <Route
                path="/invoices"
                element={
                  <FdpInvoiceList
                    FdpId={fdpDetails?.id ?? 0}
                    companyName={fdpDetails?.companyName ?? ""}
                  />
                }
              />
            )}

          {/* SFTP */}
          {checkPageAndPagePath(pagePermissions, undefined, PAGES.SFTP) && (
            <Route path="/sftp" element={<FileManager />} />
          )}
          {/* Beans Integration */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.BEANS_INTEGRATION
          ) && (
              <Route path="/beans-integration" element={<BeansIntegration />} />
            )}
          {/* Knowledge Hub */}
          {checkPageAndPagePath(
            pagePermissions,
            undefined,
            PAGES.KNOWLEDGE_HUB
          ) && (
              <Route path="/knowledge-hub" element={<KnowledgehubPage />} />
            )}
          {/* error */}
          <Route path="*" element={<Error />} />
        </Route>
        <Route
          path="/reset-password"
          element={<ResetPassword onDemandResetPassword={true} />}
        />
      </Route>
    );
  }
}
